import { Injectable } from '@angular/core';

/**
 * ═══ TELLING platform.totalum.app THAT THIS BROWSER BELONGS HERE ════════════
 *
 * The new platform (`totalum-platform`, platform.totalum.app) cannot let a user
 * whose account lives on THIS panel sign in — those accounts predate it and are
 * never migrated. Until now the platform could only discover that by making the
 * person type their email and asking the backend about it, so a returning user
 * did the same pointless round trip every visit.
 *
 * This service drops a one-byte flag the platform can read on arrival, so it can
 * send them straight back here without a single keystroke.
 *
 * ── ⚠️⚠️ WHY A COOKIE, WHEN THE SESSION ITSELF IS IN localStorage ───────────
 *
 * Because `localStorage` is per-ORIGIN and unreadable by anyone else. The
 * platform is a different host and its servers can never see it. A cookie scoped
 * to the shared parent domain is the only channel between the two apps that costs
 * nothing and works on the first request — no iframe, no redirect bounce, no
 * cross-origin call.
 *
 * ⚠️ THE LEADING DOT IS THE WHOLE TRICK. `accounts.totalum.app` and
 * `platform.totalum.app` are different hosts but share the registrable domain
 * `totalum.app`, so `Domain=.totalum.app` is sent to both. Being SAME-SITE, it is
 * unaffected by third-party-cookie deprecation and by Safari's ITP — which is
 * exactly why the obvious alternatives (a hidden iframe, a `fetch` with
 * credentials) would have quietly stopped working.
 *
 * ── ⚠️⚠️ IT IS A HINT. IT IS NOT, AND MUST NEVER BECOME, A CREDENTIAL ───────
 *
 * It holds the literal string `"1"`. It is written by this JavaScript, so anyone
 * can forge it by hand — and forging it buys exactly one thing: being shown the
 * platform's PUBLIC `/legacy-account` page. Getting an actual session here still
 * requires the 8-digit code mailed to the inbox, or a Google-verified address
 * inside a server-side hook (`legacy-handoff.service.ts` on account-backend).
 *
 * ⚠️ NO EMAIL, NO USER ID, NO TOKEN IN IT — EVER. This cookie is readable by
 * script on every *.totalum.app host, so putting an address in it would leak the
 * email of every legacy user to all of them.
 *
 * ⚠️ THE NAME AND DOMAIN ARE A CONTRACT WITH ANOTHER REPOSITORY. The platform
 * reads them in `src/lib/legacy-hint.ts` (`LEGACY_HINT_COOKIE`,
 * `LEGACY_HINT_COOKIE_DOMAIN`). Renaming either side silently switches the
 * feature off instead of breaking a build, so change both or neither.
 */

/** Must equal `LEGACY_HINT_COOKIE` in totalum-platform. */
const COOKIE_NAME = 'tot_legacy';

/** Must equal `LEGACY_HINT_COOKIE_DOMAIN` in totalum-platform. */
const COOKIE_DOMAIN = '.totalum.app';

/** Roughly a session's worth of "yes, they were signed in here". */
const MAX_AGE_SECONDS = 60 * 60 * 24 * 180; // 180 days

@Injectable({ providedIn: 'root' })
export class LegacyHintService {
  /**
   * Announce that this browser is signed in to the legacy panel.
   *
   * ⚠️ CALLED WHEREVER `localStorage.setItem('token', …)` IS. There are three such
   * places (login, sign-in flow, onboarding flow) and they must not drift — a
   * sign-in path that forgets this simply never gets the shortcut, with no error.
   */
  public mark(): void {
    this.write(`${COOKIE_NAME}=1; Max-Age=${MAX_AGE_SECONDS}`);
  }

  /**
   * ⭐⭐ ON SIGN-OUT, WRITE `0` — DO **NOT** DELETE THE COOKIE.
   *
   * ⚠️ "SIGNED OUT" AND "NO COOKIE" ARE DIFFERENT FACTS, AND ONLY ONE OF THEM IS
   * ACTIONABLE. Deleting would leave the platform knowing nothing — and it holds a
   * SECOND, first-party hint of its own (`tp_legacy`, httpOnly, 180 days) that we
   * cannot reach from this origin. It would go on speaking for the user long after
   * they left, and keep redirecting them into the account they just abandoned.
   *
   * ⚠️ THE POINT IS THE PERSON WHO SIGNS OUT IN ORDER TO START OVER. Logging out
   * of the old panel is usually step one of "let me register properly, on another
   * address". `0` is what tells the platform to stop being helpful and let them
   * reach the sign-up form — and it makes the platform clear its own hint too.
   *
   * ⚠️ SAME ATTRIBUTES AS `mark()`. A browser keys a cookie by name + domain +
   * path; getting the domain wrong writes a SECOND, host-only cookie and leaves
   * the original quietly in force.
   */
  public markSignedOut(): void {
    this.write(`${COOKIE_NAME}=0; Max-Age=${MAX_AGE_SECONDS}`);
  }

  private write(nameAndLifetime: string): void {
    // Local dev is not on totalum.app, so the browser silently rejects a cookie
    // scoped to a domain the page is not under. Skip rather than pretend.
    if (!this.onTotalumDomain()) return;

    try {
      document.cookie =
        `${nameAndLifetime}; Domain=${COOKIE_DOMAIN}; Path=/; SameSite=Lax; Secure`;
    } catch {
      // A blocked cookie store costs the shortcut and nothing else.
    }
  }

  private onTotalumDomain(): boolean {
    if (typeof document === 'undefined') return false;
    const host = window.location.hostname;
    // `endsWith` on the dotted parent, so `nottotalum.app` cannot match.
    return host === 'totalum.app' || host.endsWith(COOKIE_DOMAIN);
  }
}
